(() => {
  const drawer = document.querySelector('[data-smoothco-cart-drawer]');
  if (!drawer) return;

  const panel = drawer.querySelector('.scd__panel');
  const body = drawer.querySelector('[data-scd-body]');
  const footer = drawer.querySelector('[data-scd-footer]');
  const countNodes = [...document.querySelectorAll('[data-cart-count], [data-scd-count]')];
  const countLabel = drawer.querySelector('[data-scd-count-label]');
  const progress = drawer.querySelector('[data-scd-progress]');
  const progressCopy = drawer.querySelector('[data-scd-progress-copy]');
  const productsNode = drawer.querySelector('[data-scd-products]');
  const bundleSavingNode = drawer.querySelector('[data-scd-bundle-saving]');
  const welcomeSavingNode = drawer.querySelector('[data-scd-welcome-saving]');
  const totalNode = drawer.querySelector('[data-scd-total]');
  const checkoutTotalNode = drawer.querySelector('[data-scd-checkout-total]');
  const checkout = drawer.querySelector('[data-scd-checkout]');
  const toast = drawer.querySelector('[data-scd-toast]');

  const endpoints = {
    add: drawer.dataset.cartAddUrl || '/cart/add.js',
    change: drawer.dataset.cartChangeUrl || '/cart/change.js',
    cart: drawer.dataset.cartJsonUrl || '/cart.js',
  };

  const images = {
    bundle: drawer.dataset.bundleImage,
    toner: drawer.dataset.tonerImage,
    pads: drawer.dataset.padsImage,
  };

  const bundleReferencePrice = Number(drawer.dataset.bundleReferencePrice || 7998);
  const firstOrderEligible = drawer.dataset.firstOrderEligible !== 'false';
  let currentCart = null;
  let lastFocused = null;
  let toastTimer = null;

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  }[character]));

  const formatMoney = (cents, currency = 'USD') => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency, minimumFractionDigits: 2,
      }).format(amount);
    } catch (_) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const classify = (item) => {
    const haystack = `${item.title || ''} ${item.product_title || ''} ${item.handle || ''}`.toLowerCase();
    if (haystack.includes('complete system') || haystack.includes('bundle') || haystack.includes('strawberry routine')) return 'bundle';
    if (haystack.includes('pad')) return 'pads';
    return 'toner';
  };

  const itemImage = (item, kind) => item.featured_image?.url || item.image || images[kind] || '';

  const showToast = (message) => {
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const setBusy = (busy) => {
    drawer.classList.toggle('is-busy', busy);
    drawer.setAttribute('aria-busy', busy ? 'true' : 'false');
  };

  const openDrawer = async ({ refresh = true } = {}) => {
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('scd-open');
    window.requestAnimationFrame(() => panel?.focus());
    if (refresh) await refreshCart();
  };

  const closeDrawer = () => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('scd-open');
    lastFocused?.focus?.();
  };

  const fetchCart = async () => {
    const response = await fetch(endpoints.cart, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Could not load the cart.');
    return response.json();
  };

  const updateHeaderCount = (count) => {
    countNodes.forEach((node) => { node.textContent = count; });
    if (countLabel) countLabel.textContent = count === 1 ? 'item' : 'items';
    document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-label', `Open cart, ${count} ${count === 1 ? 'item' : 'items'}`);
      trigger.classList.remove('is-bumping');
      window.requestAnimationFrame(() => trigger.classList.add('is-bumping'));
    });
  };

  const calculateSummary = (cart) => {
    const merchandiseSubtotal = Number(
      cart.items_subtotal_price ?? cart.original_total_price ?? cart.total_price ?? 0
    );

    let productReference = 0;
    cart.items.forEach((item) => {
      const kind = classify(item);
      if (kind === 'bundle') productReference += bundleReferencePrice * item.quantity;
      else productReference += Number(item.original_line_price ?? item.line_price ?? item.final_line_price ?? 0);
    });

    productReference = Math.max(productReference, merchandiseSubtotal);
    const bundleSaving = Math.max(0, productReference - merchandiseSubtotal);

    const discountApps = [
      ...(Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
      ...(Array.isArray(cart.discount_applications) ? cart.discount_applications : []),
    ];
    const welcomeApp = discountApps.find((app) => `${app?.title || ''} ${app?.code || ''}`.toLowerCase().includes('welcome10'));
    const welcomeAlreadyApplied = Boolean(welcomeApp);
    const welcomeSaving = !firstOrderEligible
      ? 0
      : (welcomeAlreadyApplied
        ? Math.max(0, Number(welcomeApp?.total_allocated_amount || merchandiseSubtotal - Number(cart.total_price || 0)))
        : Math.round(merchandiseSubtotal * 0.10));
    const estimatedTotal = !firstOrderEligible
      ? Number(cart.total_price || merchandiseSubtotal)
      : (welcomeAlreadyApplied
        ? Number(cart.total_price || Math.max(0, merchandiseSubtotal - welcomeSaving))
        : Math.max(0, merchandiseSubtotal - welcomeSaving));

    return { productReference, bundleSaving, welcomeSaving, estimatedTotal };
  };

  const renderEmpty = () => {
    body.innerHTML = `
      <div class="scd__empty">
        <div class="scd__empty-visual"><img src="${escapeHtml(images.bundle)}" alt="Smoothco Strawberry Routine"></div>
        <h3>Your routine starts here.</h3>
        <p>Two simple steps for the details you keep checking. Real skin. No miracle claims.</p>
        <a href="/#offer" data-scd-close-link>SHOP THE ROUTINE</a>
      </div>`;
    footer.hidden = true;
  };

  const renderItems = (cart) => {
    const currency = cart.currency || 'USD';
    const items = cart.items.map((item, index) => {
      const kind = classify(item);
      const bundle = kind === 'bundle';
      const referenceUnit = bundle ? bundleReferencePrice : Number(item.original_price ?? item.price ?? item.final_price ?? 0);
      // Keep the merchandise price visible. Cart-level WELCOME10 belongs in the summary,
      // not inside the product price, otherwise the drawer appears to discount twice.
      const currentUnit = Number(item.original_price ?? item.price ?? item.final_price ?? 0);
      const savingsPercent = referenceUnit > currentUnit ? Math.round((referenceUnit - currentUnit) / referenceUnit * 100) : 0;
      const subtitle = bundle ? 'Toner + 50 Exfoliating Pads' : (kind === 'pads' ? '50 Exfoliating Pads' : 'Strawberry Exfoliating Toner');
      return `
        <article class="scd__item" style="--scd-index:${index}">
          <div class="scd__media">
            <img src="${escapeHtml(itemImage(item, kind))}" alt="${escapeHtml(item.product_title || item.title)}">
            ${bundle ? '<span class="scd__badge">BEST VALUE</span>' : ''}
          </div>
          <div class="scd__item-copy">
            <h3>${escapeHtml(item.product_title || item.title)}</h3>
            <p class="scd__subtitle">${escapeHtml(subtitle)}</p>
            <div class="scd__price">
              ${referenceUnit > currentUnit ? `<s>${formatMoney(referenceUnit, currency)}</s>` : ''}
              <strong>${formatMoney(currentUnit, currency)}</strong>
              ${savingsPercent > 0 ? `<em>SAVE ${savingsPercent}%</em>` : ''}
            </div>
            <div class="scd__item-bottom">
              <div class="scd__qty" aria-label="Quantity for ${escapeHtml(item.product_title || item.title)}">
                <button type="button" aria-label="Decrease quantity" data-scd-change data-key="${escapeHtml(item.key)}" data-quantity="${Math.max(0, item.quantity - 1)}">−</button>
                <span aria-live="polite">${item.quantity}</span>
                <button type="button" aria-label="Increase quantity" data-scd-change data-key="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}">+</button>
              </div>
              <button class="scd__remove" type="button" data-scd-change data-key="${escapeHtml(item.key)}" data-quantity="0">Remove</button>
            </div>
          </div>
        </article>`;
    }).join('');
    body.innerHTML = `<div class="scd__items">${items}</div>`;
    footer.hidden = false;
  };

  const renderCart = (cart) => {
    currentCart = cart;
    updateHeaderCount(Number(cart.item_count || 0));
    const hasItems = Number(cart.item_count || 0) > 0;
    const currency = cart.currency || 'USD';
    const freeShippingThreshold = 5000;
    const subtotal = Number(cart.total_price || cart.items_subtotal_price || 0);
    const summary = calculateSummary(cart);
    const shippingBasis = firstOrderEligible ? summary.estimatedTotal : subtotal;
    const freeShippingProgress = Math.min(100, Math.max(0, (shippingBasis / freeShippingThreshold) * 100));
    progress.style.setProperty('--scd-progress', `${freeShippingProgress}%`);
    if (shippingBasis >= freeShippingThreshold) {
      progressCopy.textContent = 'Free shipping unlocked ✦';
    } else if (shippingBasis > 0) {
      progressCopy.textContent = `You’re ${formatMoney(freeShippingThreshold - shippingBasis, currency)} away from free shipping${firstOrderEligible ? ' after first-order savings' : ''}`;
    } else {
      progressCopy.textContent = `Add ${formatMoney(freeShippingThreshold, currency)} to unlock free shipping`;
    }

    if (!hasItems) {
      renderEmpty();
      return;
    }

    renderItems(cart);
    productsNode.textContent = formatMoney(summary.productReference, currency);
    bundleSavingNode.textContent = `−${formatMoney(summary.bundleSaving, currency)}`;
    if (welcomeSavingNode) welcomeSavingNode.textContent = `−${formatMoney(summary.welcomeSaving, currency)}`;
    totalNode.textContent = formatMoney(summary.estimatedTotal, currency);
    checkoutTotalNode.textContent = formatMoney(summary.estimatedTotal, currency);
    checkout.href = drawer.dataset.checkoutUrl || '/discount/WELCOME10?redirect=%2Fcheckout';
  };

  const refreshCart = async () => {
    try {
      setBusy(true);
      renderCart(await fetchCart());
    } catch (error) {
      showToast(error.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const addToCart = async (form, submitter) => {
    const originalText = submitter?.textContent;
    try {
      setBusy(true);
      if (submitter) {
        submitter.disabled = true;
        submitter.textContent = 'ADDING…';
      }
      const formData = new FormData(form);
      const response = await fetch(endpoints.add, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
        credentials: 'same-origin',
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Could not add this product.');
      const cart = await fetchCart();
      renderCart(cart);
      await openDrawer({ refresh: false });
      showToast('Added to your Strawberry Routine 🍓');
    } catch (error) {
      showToast(error.message || 'Could not add this product.');
      await openDrawer({ refresh: true });
    } finally {
      setBusy(false);
      if (submitter) {
        submitter.disabled = false;
        submitter.textContent = originalText;
      }
    }
  };

  const changeQuantity = async (key, quantity) => {
    try {
      setBusy(true);
      const response = await fetch(endpoints.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Number(quantity) }),
        credentials: 'same-origin',
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Could not update the cart.');
      renderCart(cart);
      showToast(Number(quantity) === 0 ? 'Removed from your routine.' : 'Routine updated.');
    } catch (error) {
      showToast(error.message || 'Could not update the cart.');
    } finally {
      setBusy(false);
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    event.preventDefault();
    addToCart(form, event.submitter || form.querySelector('[type="submit"]'));
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-trigger]');
    if (trigger) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (event.target.closest('[data-scd-close]')) {
      closeDrawer();
      return;
    }

    const closeLink = event.target.closest('[data-scd-close-link]');
    if (closeLink) closeDrawer();

    const changeButton = event.target.closest('[data-scd-change]');
    if (changeButton) {
      changeQuantity(changeButton.dataset.key, changeButton.dataset.quantity);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!drawer.classList.contains('is-open')) return;
    if (event.key === 'Escape') closeDrawer();
    if (event.key !== 'Tab') return;
    const focusable = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  refreshCart();
})();
